import React from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiAlertTriangle, FiDownload, FiSend } from 'react-icons/fi';
import { calculateQuantities, convertToCommercialQuantities, getMaterialDescriptions, calculateManhours } from '../utils/quantityCalculator';
import { generateSpecificationsPDF, generateCostEstimatePDF, generateSupplierInfoPDF, generateBillOfMaterialsPDF } from '../utils/pdfGenerator';

const SyncResults = ({ data, onBack, partOneCompleted }) => {
  // Calculate quantities using the calculator
  const quantities = calculateQuantities(data);
  const commercialQuantities = convertToCommercialQuantities(quantities, data);
  const descriptions = getMaterialDescriptions(data);
  const manhours = calculateManhours(quantities);

  const submitFinal = () => {
    if (!partOneCompleted) {
      alert('Please complete Part I - Project Information first before submitting.');
      return;
    }
    alert('Form submitted!');
  };

  const downloadReport = () => {
    if (!partOneCompleted) {
      alert('Please complete Part I - Project Information first before downloading the report.');
      return;
    }
    alert('Report download started!');
  };

  const downloadCostEstimate = () => {
    if (!partOneCompleted) {
      alert('Please complete Part I - Project Information first before downloading.');
      return;
    }
    generateCostEstimatePDF(data, getCostData(), descriptions);
  };

  const downloadTabularFormat = () => {
    if (!partOneCompleted) {
      alert('Please complete Part I - Project Information first before downloading.');
      return;
    }
    generateSpecificationsPDF(data);
  };

  const downloadSupplierInfo = () => {
    if (!partOneCompleted) {
      alert('Please complete Part I - Project Information first before downloading.');
      return;
    }
    generateSupplierInfoPDF(data, getSupplierData());
  };

  const downloadBOM = () => {
    if (!partOneCompleted) {
      alert('Please complete Part I - Project Information first before downloading.');
      return;
    }
    generateBillOfMaterialsPDF(data, getBOMData());
  };

  const exportResults = () => {
    if (!partOneCompleted) {
      alert('Please complete Part I - Project Information first before exporting.');
      return;
    }
    
    // Generate all PDFs
    generateSpecificationsPDF(data);
    generateCostEstimatePDF(data, getCostData(), descriptions);
    generateSupplierInfoPDF(data, getSupplierData());
    generateBillOfMaterialsPDF(data, getBOMData());
    
    alert('All PDF reports have been generated and downloaded!');
  };

  // Generate cost data based on 2025 pricing structure
  const getCostData = () => {
    const costData = [];

    // Only add LGS components if not painted slab
    if (data.ceilingType !== 'ceiling_painted_slab') {
      // A. LGS Framing - using original quantities in proper units
      costData.push(
        { mfCode: '09 22 00', description: 'Hangers', fullDescription: descriptions.hangers, quantity: quantities.hangers, unit: 'sets', materialUnitCost: 30.75, laborUnitCost: 16.50, totalUnitCost: 47.25, totalCost: quantities.hangers * 47.25 },
        { mfCode: '09 22 00', description: 'Wall Angle', fullDescription: descriptions.wallAngle, quantity: quantities.wallAngle, unit: 'm', materialUnitCost: 36.50, laborUnitCost: 16.50, totalUnitCost: 53.00, totalCost: quantities.wallAngle * 53.00 },
        { mfCode: '09 22 00', description: 'Carrying Channel', fullDescription: descriptions.carryingChannel, quantity: quantities.carryingChannel, unit: 'm', materialUnitCost: 51.25, laborUnitCost: 33.00, totalUnitCost: 84.25, totalCost: quantities.carryingChannel * 84.25 },
        { mfCode: '09 22 00', description: 'Furring', fullDescription: descriptions.doubleFurring, quantity: quantities.doubleFurring, unit: 'm', materialUnitCost: 66.00, laborUnitCost: 33.00, totalUnitCost: 99.00, totalCost: quantities.doubleFurring * 99.00 },
        { mfCode: '09 22 00', description: 'W Clip', fullDescription: descriptions.wClips, quantity: quantities.wClips, unit: 'pcs', materialUnitCost: 7.25, laborUnitCost: 11.00, totalUnitCost: 18.25, totalCost: quantities.wClips * 18.25 }
      );

      // B. Ceiling Surface & Accessories
      let surfaceMaterialCost = 465.00; // Default gypsum 12mm
      let surfaceMfCode = '09 29 00';
      
      if (data.materialType === 'gypsum') {
        if (data.materialSpecs?.gypsumType === 'moisture_resistant') {
          surfaceMaterialCost = 680.00;
        } else {
          surfaceMaterialCost = 465.00;
        }
        surfaceMfCode = '09 29 00';
      } else if (data.materialType === 'fibercement') {
        const thickness = data.materialSpecs?.fibercementThickness;
        switch (thickness) {
          case '6mm': surfaceMaterialCost = 580.00; break;
          case '8mm': surfaceMaterialCost = 550.00; break;
          case '10mm': surfaceMaterialCost = 920.00; break;
          case '12mm': surfaceMaterialCost = 1250.00; break;
          default: surfaceMaterialCost = 580.00;
        }
        surfaceMfCode = '09 28 00';
      } else if (data.materialType === 'pvc') {
        surfaceMaterialCost = 1975.50;
        surfaceMfCode = '06 64 00';
      }

      costData.push({
        mfCode: surfaceMfCode,
        description: 'Ceiling Surface',
        fullDescription: descriptions.ceilingSurface,
        quantity: quantities.ceilingSurface,
        unit: 'm²',
        materialUnitCost: surfaceMaterialCost,
        laborUnitCost: 165.00,
        totalUnitCost: surfaceMaterialCost + 165.00,
        totalCost: quantities.ceilingSurface * (surfaceMaterialCost + 165.00)
      });

      // C. Ceiling Finish (Paints and Coatings) - only for gypsum and fibercement
      if (data.materialType === 'gypsum' || data.materialType === 'fibercement') {
        costData.push(
          { mfCode: '09 29 00', description: 'Jointing Tape & Putty', fullDescription: 'Jointing Tape & Putty', quantity: quantities.jointTapes, unit: 'm', materialUnitCost: 51.25, laborUnitCost: 16.50, totalUnitCost: 67.75, totalCost: quantities.jointTapes * 67.75 },
          { mfCode: '09 90 00', description: 'Surface Prep', fullDescription: descriptions.surfacePrep, quantity: quantities.jointTapes, unit: 'm', materialUnitCost: 0, laborUnitCost: 90.00, totalUnitCost: 90.00, totalCost: quantities.jointTapes * 90.00 },
          { mfCode: '09 90 00', description: 'Paint Base Coat', fullDescription: 'Flat Latex, single coat', quantity: quantities.paint, unit: 'm²', materialUnitCost: 175.00, laborUnitCost: 140.00, totalUnitCost: 315.00, totalCost: quantities.paint * 315.00 },
          { mfCode: '09 90 00', description: 'Paint Finish', fullDescription: 'Two coats latex/semi-flat paint', quantity: quantities.paint, unit: 'm²', materialUnitCost: 200.00, laborUnitCost: 210.00, totalUnitCost: 410.00, totalCost: quantities.paint * 410.00 }
        );
      }
    } else {
      // For painted slab - base coat and finish coat only
      costData.push(
        { mfCode: '09 90 00', description: 'Paint Base Coat', fullDescription: 'Flat Latex, single coat', quantity: quantities.paint, unit: 'm²', materialUnitCost: 175.00, laborUnitCost: 140.00, totalUnitCost: 315.00, totalCost: quantities.paint * 315.00 },
        { mfCode: '09 90 00', description: 'Paint Finish', fullDescription: 'Two coats latex/semi-flat paint', quantity: quantities.paint, unit: 'm²', materialUnitCost: 200.00, laborUnitCost: 210.00, totalUnitCost: 410.00, totalCost: quantities.paint * 410.00 }
      );
    }

    return costData;
  };

  // Get supplier data based on selected brands
  const getSupplierData = () => {
    const suppliers = [];
    
    // Always include main suppliers
    suppliers.push({
      name: 'CLC Construction Materials Corp.',
      brand: 'Eastberg / Gardner / Main Paint',
      products: 'Gypsum Boards, Fibercement Panels, Paint Systems',
      contactPerson: 'Maria Santos',
      designation: 'Sales Manager',
      contactInfo: 'Makati City, Metro Manila\nmaria.santos@clc.com.ph\n+63 917 123 4567\nhttps://clc.com.ph'
    });

    if (data.ceilingType !== 'ceiling_painted_slab') {
      suppliers.push({
        name: 'Geigher Steel Framing Systems',
        brand: 'Geigher',
        products: 'Light Gauge Steel Framing, Suspension Systems',
        contactPerson: 'Roberto Cruz',
        designation: 'Technical Manager',
        contactInfo: 'Quezon City, Metro Manila\nroberto.cruz@geigher.com\n+63 918 234 5678\nhttps://clc.com.ph/geigher'
      });
    }

    return suppliers;
  };

  // Generate BOM data based on COMMERCIAL quantities for purchasing
  const getBOMData = () => {
    const bomData = [];
    let itemNo = 1;

    // Only add LGS components if not painted slab
    if (data.ceilingType !== 'ceiling_painted_slab') {
      // A. LGS Framing - using commercial quantities for purchasing
      const lgsItems = [
        { 
          description: descriptions.hangers,
          supplier: 'Geigher Steel Framing Systems',
          qty: commercialQuantities.hangers,
          uom: 'SETS'
        },
        { 
          description: descriptions.wallAngle,
          supplier: 'Geigher Steel Framing Systems',
          qty: commercialQuantities.wallAngle,
          uom: 'PCS'
        },
        { 
          description: descriptions.carryingChannel,
          supplier: 'Geigher Steel Framing Systems',
          qty: commercialQuantities.carryingChannel,
          uom: 'PCS'
        },
        { 
          description: descriptions.doubleFurring,
          supplier: 'Geigher Steel Framing Systems',
          qty: commercialQuantities.doubleFurring,
          uom: 'PCS'
        },
        { 
          description: descriptions.wClips,
          supplier: 'Geigher Steel Framing Systems',
          qty: commercialQuantities.wClips,
          uom: 'PCS'
        }
      ];

      lgsItems.forEach(item => {
        bomData.push({
          itemNo: String(itemNo).padStart(3, '0'),
          description: item.description,
          supplier: item.supplier,
          qty: item.qty,
          uom: item.uom,
          unitPrice: '',
          amount: ''
        });
        itemNo++;
      });

      // B. Ceiling Surface & Accessories
      if (descriptions.ceilingSurface) {
        bomData.push({
          itemNo: String(itemNo).padStart(3, '0'),
          description: descriptions.ceilingSurface,
          supplier: 'CLC Construction Materials Corp.',
          qty: commercialQuantities.ceilingSurface,
          uom: 'PCS',
          unitPrice: '',
          amount: ''
        });
        itemNo++;
      }

      // C. Ceiling Finish (Paints and Coatings) - only for gypsum and fibercement
      if (data.materialType === 'gypsum' || data.materialType === 'fibercement') {
        const finishItems = [
          {
            description: `${descriptions.jointingTape} Paper Joint Tape ${data.materialSpecs?.tapeOrderSize || '75m'}`,
            supplier: 'CLC Construction Materials Corp.',
            qty: commercialQuantities.jointingTape,
            uom: 'ROLLS'
          },
          {
            description: `${descriptions.jointingPutty} Jointing Compound`,
            supplier: 'CLC Construction Materials Corp.',
            qty: commercialQuantities.jointingPutty,
            uom: 'KG'
          },
          {
            description: `Sand Paper 120 Grit`,
            supplier: 'CLC Construction Materials Corp.',
            qty: commercialQuantities.sandPaper,
            uom: 'PCS'
          },
          {
            description: `${descriptions.baseCoat} ${data.materialSpecs?.baseCoatOrderSize || '4L'}`,
            supplier: 'CLC Construction Materials Corp.',
            qty: commercialQuantities.paintBase,
            uom: 'CANS'
          },
          {
            description: `${descriptions.finishCoat} ${data.materialSpecs?.finishCoatOrderSize || '4L'}`,
            supplier: 'CLC Construction Materials Corp.',
            qty: commercialQuantities.paintFinish,
            uom: 'CANS'
          }
        ];

        finishItems.forEach(item => {
          bomData.push({
            itemNo: String(itemNo).padStart(3, '0'),
            description: item.description,
            supplier: item.supplier,
            qty: item.qty,
            uom: item.uom,
            unitPrice: '',
            amount: ''
          });
          itemNo++;
        });
      }
    } else {
      // For painted slab - base coat and finish coat only
      const paintItems = [
        {
          description: `${descriptions.baseCoat} ${data.materialSpecs?.baseCoatOrderSize || '4L'}`,
          supplier: 'CLC Construction Materials Corp.',
          qty: commercialQuantities.paintBase,
          uom: 'CANS'
        },
        {
          description: `${descriptions.finishCoat} ${data.materialSpecs?.finishCoatOrderSize || '4L'}`,
          supplier: 'CLC Construction Materials Corp.',
          qty: commercialQuantities.paintFinish,
          uom: 'CANS'
        }
      ];

      paintItems.forEach(item => {
        bomData.push({
          itemNo: String(itemNo).padStart(3, '0'),
          description: item.description,
          supplier: item.supplier,
          qty: item.qty,
          uom: item.uom,
          unitPrice: '',
          amount: ''
        });
        itemNo++;
      });
    }

    return bomData;
  };

  const costData = getCostData();
  const supplierData = getSupplierData();
  const bomData = getBOMData();
  const totalProjectCost = costData.reduce((sum, item) => sum + item.totalCost, 0);

  return (
    <section className="card grid gap-4">
      <h2 className="text-base font-semibold">Part III — Sync Results</h2>

      {!partOneCompleted && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <SafeIcon icon={FiAlertTriangle} className="text-amber-500" size={20} />
          <div>
            <div className="font-semibold text-amber-800">Project Information Incomplete</div>
            <div className="text-sm text-amber-700">
              Please complete all required fields in Part I before viewing results or submitting.
            </div>
          </div>
        </div>
      )}

      <div className={partOneCompleted ? '' : 'opacity-75 pointer-events-none'}>
        
        {/* 3.1 MATERIAL REQUIREMENTS CHECK */}
        <div className="card p-3 mb-4">
          <h3 className="font-semibold mb-3">3.1. MATERIAL REQUIREMENTS CHECK</h3>
          <div className="grid gap-3">
            <div className="p-3 border border-green-200 rounded-lg bg-green-50">
              <div className="font-semibold text-green-800">✓ Space Classification: {data.selectedSAC || 'Not selected'}</div>
              <div className="text-sm text-green-600 mt-1">SAC validation completed successfully</div>
            </div>
            <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
              <div className="font-semibold text-blue-800">✓ Ceiling Type: {data.ceilingType || 'Not selected'}</div>
              <div className="text-sm text-blue-600 mt-1">Ceiling configuration validated</div>
            </div>
            <div className="p-3 border border-purple-200 rounded-lg bg-purple-50">
              <div className="font-semibold text-purple-800">✓ Accent Configuration: {data.accentConfig || 'Not selected'}</div>
              <div className="text-sm text-purple-600 mt-1">Design requirements confirmed</div>
            </div>
            {data.areaSqm && (
              <div className="p-3 border border-orange-200 rounded-lg bg-orange-50">
                <div className="font-semibold text-orange-800">✓ Area Calculation: {data.areaSqm} SQM</div>
                <div className="text-sm text-orange-600 mt-1">Material quantities calculated based on area</div>
              </div>
            )}
            {data.materialSpecs?.allowances && (
              <div className="p-3 border border-green-200 rounded-lg bg-green-50">
                <div className="font-semibold text-green-800">✓ Allowances Applied:</div>
                <div className="text-sm text-green-600 mt-1">
                  {data.materialSpecs.allowances.lgsFraming && `LGS Framing: ${data.materialSpecs.allowances.lgsFraming}% • `}
                  {data.materialSpecs.allowances.ceilingSurface && `Ceiling Surface: ${data.materialSpecs.allowances.ceilingSurface}% • `}
                  {data.materialSpecs.allowances.paintCoatings && `Paint & Coatings: ${data.materialSpecs.allowances.paintCoatings}%`}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 3.2 C3030 CEILING ASSEMBLY SPECIFICATIONS */}
        <div className="card p-3 mb-4">
          <h3 className="font-semibold mb-3">3.2. C3030 SPECIFICATIONS</h3>
          <div className="bg-white border border-gray-300 p-4 min-h-[200px]">
            
            {/* Project Information Header */}
            <div style={{ marginBottom: '16px', padding: '8px', background: '#f8f9fa', border: '1px solid #dee2e6' }}>
              <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '11pt', fontWeight: 600, marginBottom: '8px' }}>PROJECT INFORMATION</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ fontSize: '10pt', lineHeight: 1.4 }}>
                <div>
                  <div style={{ marginBottom: '4px' }}><strong>Project Name:</strong> {data.projectName || 'Not specified'}</div>
                  <div style={{ marginBottom: '4px' }}><strong>Project Owner:</strong> {data.owner || 'Not specified'}</div>
                  <div style={{ marginBottom: '4px' }}><strong>Occupancy:</strong> {data.occupancy || 'Not specified'}</div>
                  <div><strong>Start Date:</strong> {data.startDate || 'Not specified'}</div>
                </div>
                <div>
                  <div style={{ marginBottom: '4px' }}><strong>Location:</strong> {data.location || 'Not specified'}</div>
                  <div style={{ marginBottom: '4px' }}><strong>Room Type:</strong> {data.roomType || 'Not specified'}</div>
                  <div style={{ marginBottom: '4px' }}><strong>Area (sqm):</strong> {data.areaSqm || 'Not specified'}</div>
                  <div><strong>Issue Date:</strong> {new Date().toLocaleDateString()}</div>
                </div>
              </div>
            </div>

            {/* Element/Sub-Element Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #000', padding: '8px', background: '#f8f9fa', fontFamily: 'Montserrat, sans-serif', fontSize: '10pt', fontWeight: 600, textAlign: 'center', width: '25%' }}>Element / Sub-Element</th>
                  <th style={{ border: '1px solid #000', padding: '8px', background: '#f8f9fa', fontFamily: 'Montserrat, sans-serif', fontSize: '10pt', fontWeight: 600, textAlign: 'center', width: '75%' }}>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '8px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600, verticalAlign: 'top' }}>
                    Light Gauge Steel Framing (LGS framing members for suspended or framed ceilings)
                  </td>
                  <td style={{ border: '1px solid #000', padding: '8px', fontSize: '9pt', lineHeight: 1.3, textAlign: 'justify' }}>
                    Hanger with Clips and Brackets: Solid rod, threaded rod, or suspension wire; drop lengths up to 300 mm, 301–600 mm, or 601–900 mm. Wall Angle: Single or double (shadow gap); thickness 0.6–1.0 mm. Carrying Channel: Thickness 0.6–1.0 mm. Furring: Single or double; thickness 0.4–0.6 mm. Clips: W-clip or wire coupling clip.
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '8px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600, verticalAlign: 'top' }}>
                    Ceiling Surface & Accessories (primary visible finish layer)
                  </td>
                  <td style={{ border: '1px solid #000', padding: '8px', fontSize: '9pt', lineHeight: 1.3, textAlign: 'justify' }}>
                    Painted Fiber Cement Board: Factory-finished; corrosion-resistant fasteners; installed to manufacturer's specifications. Fiber Cement Board Screws (32–45 mm): Corrosion-resistant; automatically selected with painted fiber cement. Painted Gypsum Board: Factory-finished; installed to manufacturer's specifications. Gypsum Board Screws (25–32 mm): Black phosphate; automatically selected with painted gypsum board. PVC Ceiling Panels: As specified; moisture-resistant and washable surface.
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '8px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600, verticalAlign: 'top' }}>
                    Ceiling Finish – Paints and Coatings (final decorative and protective treatment)
                  </td>
                  <td style={{ border: '1px solid #000', padding: '8px', fontSize: '9pt', lineHeight: 1.3, textAlign: 'justify' }}>
                    Base Coat: Always flat white primer/base coat as specified by manufacturer. Jointing Tape: As selected, with main and two alternate approved brands. Jointing Putty: As selected, with main and two alternate approved brands. Finish Coat: Interior-grade paint, compliant with Master Painters Institute (MPI) standards for substrate type and gloss level. All coats from a single manufacturer; VOC limits per MPI GPS-1-12.
                  </td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #000', padding: '8px', fontFamily: 'Montserrat, sans-serif', fontWeight: 600, verticalAlign: 'top' }}>
                    Allowances (waste and installation contingencies)
                  </td>
                  <td style={{ border: '1px solid #000', padding: '8px', fontSize: '9pt', lineHeight: 1.3, textAlign: 'justify' }}>
                    Material allowances as specified: LGS Framing allowance for cutting waste and installation adjustments. Ceiling Surface allowance for breakage and complex cuts. Paint and Coatings allowance for touch-ups and application losses. Allowances range from 5% (minimal waste) to 20% (complex installation).
                  </td>
                </tr>
              </tbody>
            </table>

            <div style={{ textAlign: 'center', fontSize: '8pt', color: '#666', fontStyle: 'italic', marginTop: '16px' }}>
              Specification generated based on selected materials and configurations from Part II
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <button className="btn btn-primary" onClick={downloadTabularFormat}>
              <SafeIcon icon={FiDownload} className="inline mr-1" />
              Download Specifications PDF
            </button>
          </div>
        </div>

        {/* 3.3 BILL OF QUANTITIES / COST AND RESOURCE ESTIMATE */}
        <div className="card p-3 mt-4">
          <h3 className="font-semibold mb-3">3.3. BILL OF QUANTITIES / COST AND RESOURCE ESTIMATE</h3>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>MF-CODES</th>
                  <th>Material</th>
                  <th>Description</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Material Cost/Unit</th>
                  <th>Labor Cost/Unit</th>
                  <th>Total Cost/Unit</th>
                  <th>Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.ceilingType !== 'ceiling_painted_slab' && (
                  <>
                    <tr style={{ background: '#f3f4f6', fontWeight: 600 }}>
                      <td colSpan="9" style={{ textAlign: 'left' }}>A. LGS Framing</td>
                    </tr>
                    {costData.slice(0, 5).map((item, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: 'center' }}>{item.mfCode}</td>
                        <td style={{ textAlign: 'left' }}>{item.description}</td>
                        <td style={{ textAlign: 'left' }}>{item.fullDescription}</td>
                        <td style={{ textAlign: 'center' }}>{item.quantity.toFixed(2)}</td>
                        <td style={{ textAlign: 'center' }}>{item.unit}</td>
                        <td style={{ textAlign: 'right' }}>₱{item.materialUnitCost.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>₱{item.laborUnitCost.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>₱{item.totalUnitCost.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>₱{item.totalCost.toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#f3f4f6', fontWeight: 600 }}>
                      <td colSpan="9" style={{ textAlign: 'left' }}>B. Ceiling Surface & Accessories</td>
                    </tr>
                    {costData.filter(item => item.description.includes('Ceiling Surface')).map((item, index) => (
                      <tr key={`surface-${index}`}>
                        <td style={{ textAlign: 'center' }}>{item.mfCode}</td>
                        <td style={{ textAlign: 'left' }}>{item.description}</td>
                        <td style={{ textAlign: 'left' }}>{item.fullDescription}</td>
                        <td style={{ textAlign: 'center' }}>{item.quantity.toFixed(2)}</td>
                        <td style={{ textAlign: 'center' }}>{item.unit}</td>
                        <td style={{ textAlign: 'right' }}>₱{item.materialUnitCost.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>₱{item.laborUnitCost.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>₱{item.totalUnitCost.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>₱{item.totalCost.toLocaleString()}</td>
                      </tr>
                    ))}
                  </>
                )}
                
                <tr style={{ background: '#f3f4f6', fontWeight: 600 }}>
                  <td colSpan="9" style={{ textAlign: 'left' }}>C. Ceiling Finish (Paints and Coatings)</td>
                </tr>
                {costData.filter(item => 
                  item.description.includes('Jointing') || 
                  item.description.includes('Surface Prep') || 
                  item.description.includes('Paint')
                ).map((item, index) => (
                  <tr key={`finish-${index}`}>
                    <td style={{ textAlign: 'center' }}>{item.mfCode}</td>
                    <td style={{ textAlign: 'left' }}>{item.description}</td>
                    <td style={{ textAlign: 'left' }}>{item.fullDescription}</td>
                    <td style={{ textAlign: 'center' }}>{item.quantity.toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>{item.unit}</td>
                    <td style={{ textAlign: 'right' }}>₱{item.materialUnitCost.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>₱{item.laborUnitCost.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>₱{item.totalUnitCost.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>₱{item.totalCost.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f3f4f6', fontWeight: 600 }}>
                  <td colSpan="8" style={{ textAlign: 'right' }}>ESTIMATED DIRECT COST FOR C3030 CEILING FINISHES</td>
                  <td style={{ textAlign: 'right' }}>₱{totalProjectCost.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ACTIVITY DURATION (in Manhours) */}
          <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="font-semibold mb-3">ACTIVITY DURATION (in Manhours)</h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Activity</th>
                    <th style={{ textAlign: 'center' }}>Rate</th>
                    <th style={{ textAlign: 'center' }}>Manhours</th>
                    <th style={{ textAlign: 'right' }}>Labor Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ceilingType !== 'ceiling_painted_slab' && (
                    <>
                      <tr>
                        <td>FRAMING</td>
                        <td style={{ textAlign: 'center' }}>4 MH/m²</td>
                        <td style={{ textAlign: 'center' }}>{manhours.framing} MH</td>
                        <td style={{ textAlign: 'right' }}>₱500/MH</td>
                      </tr>
                      <tr>
                        <td>BOARDS</td>
                        <td style={{ textAlign: 'center' }}>2 MH/m²</td>
                        <td style={{ textAlign: 'center' }}>{manhours.boards} MH</td>
                        <td style={{ textAlign: 'right' }}>₱500/MH</td>
                      </tr>
                    </>
                  )}
                  {(data.materialType === 'gypsum' || data.materialType === 'fibercement') && (
                    <tr>
                      <td>SURFACE PREP</td>
                      <td style={{ textAlign: 'center' }}>1 MH/m</td>
                      <td style={{ textAlign: 'center' }}>{manhours.surfacePrep} MH</td>
                      <td style={{ textAlign: 'right' }}>₱90/m</td>
                    </tr>
                  )}
                  <tr>
                    <td>BASE PAINT (1 coat)</td>
                    <td style={{ textAlign: 'center' }}>1.5 MH/m²</td>
                    <td style={{ textAlign: 'center' }}>{manhours.basePaint} MH</td>
                    <td style={{ textAlign: 'right' }}>₱140/m²</td>
                  </tr>
                  <tr>
                    <td>FINISH PAINT (2 coats)</td>
                    <td style={{ textAlign: 'center' }}>2.5 MH/m²</td>
                    <td style={{ textAlign: 'center' }}>{manhours.finishPaint} MH</td>
                    <td style={{ textAlign: 'right' }}>₱210/m²</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end mt-3">
            <button className="btn btn-primary" onClick={downloadCostEstimate}>
              <SafeIcon icon={FiDownload} className="inline mr-1" />
              Download Cost Estimate PDF
            </button>
          </div>
        </div>

        {/* 3.4 SUPPLIER & SOURCE INFORMATION */}
        <div className="card p-3 mt-4">
          <h3 className="font-semibold mb-3">3.4. SUPPLIER & SOURCE INFORMATION</h3>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>SUPPLIER NAME</th>
                  <th>BRAND</th>
                  <th>PRODUCTS</th>
                  <th>CONTACT PERSON</th>
                  <th>DESIGNATION</th>
                  <th>CONTACT INFO</th>
                </tr>
              </thead>
              <tbody>
                {supplierData.map((supplier, index) => (
                  <tr key={index}>
                    <td style={{ textAlign: 'left', fontWeight: 600 }}>{supplier.name}</td>
                    <td style={{ textAlign: 'center' }}>{supplier.brand}</td>
                    <td style={{ textAlign: 'left' }}>{supplier.products}</td>
                    <td style={{ textAlign: 'left' }}>{supplier.contactPerson}</td>
                    <td style={{ textAlign: 'left' }}>{supplier.designation}</td>
                    <td style={{ textAlign: 'left', fontSize: '12px', lineHeight: 1.4 }}>
                      {supplier.contactInfo.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}<br />
                        </React.Fragment>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-3">
            <button className="btn btn-primary" onClick={downloadSupplierInfo}>
              <SafeIcon icon={FiDownload} className="inline mr-1" />
              Download Supplier Info PDF
            </button>
          </div>
        </div>

        {/* 3.5 BILL OF MATERIALS / REQUEST FOR QUOTATION - Using COMMERCIAL quantities */}
        <div className="card p-3 mt-4">
          <h3 className="font-semibold mb-3">3.5. BILL OF MATERIALS / REQUEST FOR QUOTATION</h3>
          <p className="note mb-3">Complete list of materials required for the project - Checklist format with supplier information (Commercial purchasing units)</p>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th>☐</th>
                  <th>ITEM NO.</th>
                  <th>DESCRIPTION</th>
                  <th>SUPPLIER</th>
                  <th>QTY</th>
                  <th>UOM</th>
                  <th>UNIT PRICE</th>
                  <th>AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {bomData.map((item, index) => (
                  <tr key={index}>
                    <td style={{ textAlign: 'center', width: '30px' }}>☐</td>
                    <td style={{ textAlign: 'center' }}>{item.itemNo}</td>
                    <td style={{ textAlign: 'left' }}>{item.description}</td>
                    <td style={{ textAlign: 'left', fontSize: '12px' }}>{item.supplier}</td>
                    <td style={{ textAlign: 'center' }}>{item.qty}</td>
                    <td style={{ textAlign: 'center' }}>{item.uom}</td>
                    <td style={{ textAlign: 'right' }}>{item.unitPrice || '___________'}</td>
                    <td style={{ textAlign: 'right' }}>{item.amount || '___________'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f3f4f6', fontWeight: 600 }}>
                  <td colSpan="7" style={{ textAlign: 'right' }}>TOTAL AMOUNT</td>
                  <td style={{ textAlign: 'right' }}>___________</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="flex justify-end mt-3">
            <button className="btn btn-primary" onClick={downloadBOM}>
              <SafeIcon icon={FiDownload} className="inline mr-1" />
              Download Bill of Materials PDF
            </button>
          </div>
        </div>

      </div>

      <div className="flex justify-between gap-2 mt-4">
        <button className="btn" onClick={onBack}>← Back to Part II</button>
        <div className="flex gap-2">
          <button 
            className={`btn flex items-center gap-2 ${!partOneCompleted ? 'opacity-75' : ''}`} 
            onClick={downloadReport}
          >
            <SafeIcon icon={FiDownload} />
            Download Report
          </button>
          <button 
            className={`btn btn-primary flex items-center gap-2 ${!partOneCompleted ? 'opacity-75' : ''}`} 
            onClick={exportResults}
          >
            <SafeIcon icon={FiSend} />
            Export All Results
          </button>
        </div>
      </div>
    </section>
  );
};

export default SyncResults;